import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CurrentUserModel } from '../models/currentuser';
import { catchError, map } from 'rxjs/operators';
import { ConstantsProviders } from '../providers/constants/constants';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { ShippingReceiveDepartmentService } from '../../features/shipping-receive/services/department.service';
import { PackingTrackerDepartmentService } from '../../features/packing-tracker/services/department.service';
import { CompressionDepartmentService } from '../../features/compression/services/department.service';
import { PleatPackDepartmentService } from '../../features/pleat-packs/services/department.service';
import { CompoundingDepartmentService } from '../../features/compounding/services/department.service';
import { CNCDepartmentService } from '../../features/cnc/services/department.service';
import { LidDepartmentService } from '@dep-lids/services';
import { HpsDepartmentService } from '../../features/hps/services/department.service';
import { PlasticDepartmentService } from '../../features/plastic/services/department.service';
import { HosesDepartmentService } from '@dep-hoses/services/hoses.service';
import { InjectionMoldingDepartmentService } from '../../features/injection-molding/services/department.service';
import { PolyurethaneDepartmentService } from '../../features/polyurethane/services/department.service';
import { FilterDepartmentService } from '@dep/filters/services';
import { ApiRouteUserService } from '../../api/elite/routes/users.service';
import { TankDepartmentService } from '@dep-tanks/services';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly shippingReceiveDepartment = inject(
    ShippingReceiveDepartmentService
  );
  private readonly packingTrackerDepartment = inject(
    PackingTrackerDepartmentService
  );
  private readonly apiUserService = inject(ApiRouteUserService);
  private readonly compressionDepartment = inject(CompressionDepartmentService);
  private readonly tanksDepartment = inject(TankDepartmentService);
  private readonly pleatPacksDepartment = inject(PleatPackDepartmentService);
  private readonly compoundingDepartment = inject(CompoundingDepartmentService);
  private readonly cncDepartmentService = inject(CNCDepartmentService);
  private readonly lidsDepartmentService = inject(LidDepartmentService);
  private readonly hpsDepartmentService = inject(HpsDepartmentService);
  private readonly plasticDepartment = inject(PlasticDepartmentService);
  private readonly hosesDepartment = inject(HosesDepartmentService);
  private readonly injectionMoldingDepartment = inject(
    InjectionMoldingDepartmentService
  );
  private readonly polyurethaneDepartment = inject(
    PolyurethaneDepartmentService
  );
  private readonly filtersDepartmentService = inject(FilterDepartmentService);

  constructor(
    public http: HttpClient,
    public constants: ConstantsProviders,
    private authService: MsalService,
    private router: Router
  ) {}

  private currentUser = new BehaviorSubject<CurrentUserModel>(null);

  // currentUser: CurrentUserModel;

  private onError(err: any): any {
    if (err.error.error_codes[0]) {
      return err.error.error_codes[0];
    } else if (err.error_codes[0]) {
      return err.error_codes[0];
    } else {
      return false;
    }
  }

  signOnUser(user: {
    uid: string;
    firstName: string;
    lastName: string;
    email: string;
  }): Observable<any> {
    return this.apiUserService
      .addUser({
        firstName: user.firstName,
        lastName: user.lastName,
        uid: user.uid,
        email: user.email,
        assignations: [],
        emailDestination: null,
        isInternal: false,
        employeeNumber: null,
      })
      .pipe(
        map((response: any) => response.user),
        catchError(async (err) => this.onError(err))
      );
  }

  setActiveUser(user: CurrentUserModel): void {
    this.currentUser.next(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.redirectRoute();
  }

  public loggedIn(): boolean {
    const user = this.authService.instance.getActiveAccount();
    return !!user;
  }

  public getUserRole(): string[] {
    return this.authService.instance.getActiveAccount().idTokenClaims[`roles`];
  }

  public redirectRoute(): void {
    const roles = this.getUserRole();
    if (
      roles.includes('Chief') ||
      roles.includes('Scanner') ||
      roles.includes('ScannerOperators') ||
      roles.includes('Admin')
    ) {
      this.router.navigate(['sb']);
      return;
    }
    if (this.plasticDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.hosesDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.injectionMoldingDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.polyurethaneDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.shippingReceiveDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.packingTrackerDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.compressionDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.tanksDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.pleatPacksDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.compoundingDepartment.defaultRoutes(roles)) {
      return;
    }
    if (this.cncDepartmentService.defaultRoutes(roles)) {
      return;
    }
    if (this.lidsDepartmentService.defaultRoutes(roles)) {
      return;
    }
    if (this.hpsDepartmentService.defaultRoutes(roles)) {
      return;
    }
    if (this.filtersDepartmentService.defaultRoutes(roles)) {
      return;
    }

    // if (role.includes('Plastic') || role.includes('Inspection')) {
    //   this.router.navigate(['sb/plastic/machine']);
    //   return;
    // }

    if (roles.includes('PrivateLabels')) {
      this.router.navigate(['sb/pl/prepares']);
    }
    if (roles.includes('HumanResources')) {
      this.router.navigate(['sb/users']);
    }

    if (roles.includes('PowerBiViewer') || roles.includes('PowerBiGeneral')) {
      this.router.navigate(['sb/reportsPBI']);
    }

    if (roles.includes('ShipHawk')) {
      this.router.navigate(['sb/shiphawk/generate-excel']);
    }
    // Hoses routes

    if (
      roles.includes('Nave4Viewer') ||
      roles.includes('Nave4Operator') ||
      roles.includes('Nave4Leader')
    ) {
      this.router.navigate(['sb/nave4/scheduler']);
      return;
    }
    if (roles.includes('ClientPBIRotomolding')) {
      this.router.navigate(['sb/reportsPBI/clients/rotomolding']);
    }
    if (
      roles.includes('PowerBiWsSales') ||
      roles.includes('SalesRepresentative')
    ) {
      this.router.navigate(['sb/reportsPBI/sales/workbook']);
    }
    if (roles.includes('QualityControlInspector')) {
      this.router.navigate(['sb/hoses/quality-control']);
    }

    // if (roles.includes('PowerBiWsNetsuite')) {
    //   this.router.navigate(['sb/reportsPBI/netsuite/departments']);
    // }
    if (roles.includes('PowerBiWsEliteAdmin')) {
      this.router.navigate(['sb/reportsPBI/elite-admin/overtime']);
    }
  }
}
